"use client";

import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import type { BlockProps, StrapiBlock } from "@/app/components/blocks/types";

const AUTOPLAY_DELAY = 4000;

type Media = {
  id?: number;
  documentId?: string;
  url?: string | null;
  alternativeText?: string | null;
};

type Partenaire = {
  id?: number;
  documentId?: string;
  image?: Media | null;
  url?: string | null;
};

type PartenaireLocalBlock = StrapiBlock & {
  __component: "blocks.partenaire-local";
  titre?: string | null;
  partenaires?: Partenaire[];
};

function splitIntoPages<T>(items: T[], size: number): T[][] {
  if (items.length === 0 || size <= 0) return [];

  const pageCount = Math.ceil(items.length / size);
  const itemsVisible = Math.min(size, items.length);

  return Array.from({ length: pageCount }, (_, pageIndex) => {
    const startIndex = pageIndex * size;

    return Array.from({ length: itemsVisible }, (_, itemIndex) => {
      const index = (startIndex + itemIndex) % items.length;
      return items[index];
    });
  });
}

export default function PartenaireLocal({
  data,
}: BlockProps<PartenaireLocalBlock>) {
  const partenaires = Array.isArray(data.partenaires)
    ? data.partenaires
    : [];

  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [activePage, setActivePage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth < 640) {
        setItemsPerPage(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerPage(2);
      } else {
        setItemsPerPage(4);
      }
    };

    updateItemsPerPage();

    window.addEventListener("resize", updateItemsPerPage);

    return () => {
      window.removeEventListener("resize", updateItemsPerPage);
    };
  }, []);

  const pages = useMemo(
    () => splitIntoPages(partenaires, itemsPerPage),
    [partenaires, itemsPerPage],
  );

  useEffect(() => {
    if (activePage >= pages.length) {
      setActivePage(0);
    }
  }, [activePage, pages.length]);

  useEffect(() => {
    if (pages.length <= 1 || isPaused) return;

    const timer = window.setTimeout(() => {
      setActivePage((currentPage) => (currentPage + 1) % pages.length);
    }, AUTOPLAY_DELAY);

    return () => {
      window.clearTimeout(timer);
    };
  }, [activePage, isPaused, pages.length]);

  const showPreviousPage = () => {
    setActivePage((currentPage) =>
      currentPage === 0 ? pages.length - 1 : currentPage - 1,
    );
  };

  const showNextPage = () => {
    setActivePage((currentPage) => (currentPage + 1) % pages.length);
  };

  if (partenaires.length === 0) return null;

  return (
    <section className="bg-white py-10 lg:py-14">
      <div className="lms-container">
        <div
          className="relative mx-auto max-w-[1200px] overflow-hidden rounded-[22px] px-12 py-12 sm:px-16 lg:px-20"
          style={{
            backgroundImage:
              "url('https://site-lms.fra1.digitaloceanspaces.com/Frame_549_14ce13fff8.png')",
          }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* TITRE */}
          {data.titre && (
            <h2 className="relative z-10 mb-10 text-center font-playfair text-4xl font-bold text-white lg:text-[44px] 2xl:text-5xl">
              {data.titre}
            </h2>
          )}

          {/* CARROUSEL */}
          <div className="relative z-10 overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${activePage * 100}%)`,
              }}
            >
              {pages.map((page, pageIndex) => (
                <div
                  key={pageIndex}
                  className="grid min-w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
                >
                  {page.map((partenaire, index) => {
                    const imageUrl = partenaire.image?.url || "";
                    const imageAlt =
                      partenaire.image?.alternativeText ||
                      "Partenaire local";
                    const href = partenaire.url || "#";
                    const isExternalLink = href.startsWith("http");

                    return (
                      <Link
                        key={
                          partenaire.documentId ??
                          partenaire.id ??
                          `${pageIndex}-${index}`
                        }
                        href={href}
                        target={isExternalLink ? "_blank" : undefined}
                        rel={
                          isExternalLink
                            ? "noopener noreferrer"
                            : undefined
                        }
                        className="group flex h-[150px] items-center justify-center overflow-hidden rounded-xl bg-white px-7 py-6 shadow-sm transition duration-300  hover:shadow-lg"
                        aria-label={`Accéder au site de ${imageAlt}`}
                      >
                        {imageUrl ? (
                          <div className="relative h-full w-full">
                            <Image
                              src={imageUrl}
                              alt={imageAlt}
                              fill
                              className="object-contain transition-transform duration-300 group-hover:scale-105"
                              sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 250px"
                            />
                          </div>
                        ) : (
                          <span className="text-center font-medium text-textColor">
                            Partenaire local
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* FLÈCHE GAUCHE */}
          {pages.length > 1 && (
            <button
              type="button"
              onClick={showPreviousPage}
              aria-label="Afficher les partenaires précédents"
              className="absolute left-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-primaryGreen shadow-md transition hover:scale-105 hover:bg-greenPastel lg:left-5"
            >
              <ChevronLeft size={27} strokeWidth={2.5} />
            </button>
          )}

          {/* FLÈCHE DROITE */}
          {pages.length > 1 && (
            <button
              type="button"
              onClick={showNextPage}
              aria-label="Afficher les partenaires suivants"
              className="absolute right-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-primaryGreen shadow-md transition hover:scale-105 hover:bg-greenPastel lg:right-5"
            >
              <ChevronRight size={27} strokeWidth={2.5} />
            </button>
          )}

          {/* INDICATEURS */}
          {pages.length > 1 && (
            <div className="relative z-10 mt-8 flex justify-center gap-2">
              {pages.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setActivePage(index)}
                  aria-label={`Afficher la page ${index + 1} des partenaires`}
                  aria-current={
                    activePage === index ? "true" : undefined
                  }
                  className={`h-3 w-3 rounded-full border transition ${
                    activePage === index
                      ? "border-primaryGreen bg-primaryGreen"
                      : "border-white bg-transparent"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}